import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useTransition } from 'react';
import classNames from 'classnames';
import type { EpgChannel, EpgProgram } from '@jwp/ott-common/types/epg';
import Image from '@jwp/ott-ui-react/src/components/Image/Image';
import LoadingOverlay from '@jwp/ott-ui-react/src/components/LoadingOverlay/LoadingOverlay';
import { getProgramClosestToSameTime, programIsLive } from '@jwp/ott-common/src/utils/epg';

import EpgTimeline from './EpgTimeline';
import styles from './EpgVerticalGuide.module.scss';

type Props = {
  channel: EpgChannel | undefined;
  isFetching: boolean;
  channelsScrollbarHidden?: boolean;
  channels: EpgChannel[];
  selectedDayOffset: number;
  setChannel: (channelId: string) => void;
  onProgramClick: (program: EpgProgram) => void;
};

const SWIPE_THRESHOLD = 60;
const GRACE_DELTA = 8;

const EpgVerticalGuide: React.FC<Props> = ({ channel: activeChannel, channels, isFetching, channelsScrollbarHidden, setChannel, onProgramClick }: Props) => {
  const [, startChannelTransition] = useTransition(); // @todo is this still needed?
  const [channelChangeSource, setChannelChangeSource] = useState<'swipe' | 'click' | 'load'>('load');
  const programContainerRef = useRef<HTMLDivElement>(null);
  const channelContainerRef = useRef<HTMLDivElement>(null);
  const programsRefs = useRef<(HTMLDivElement | null)[]>([]);
  const activeChannelRef = useRef<HTMLButtonElement>(null);
  const gestureRef = useRef({
    startX: 0,
    startY: 0,
    cancelled: false,
    locked: false,
  });
  const channelsClassnames = classNames(styles.channels, {
    [styles.hideScrollbar]: channelsScrollbarHidden,
  });

  const mostRelevantProgramIndex = useMemo(() => {
    // try to find a live program first, fallback closest to same time
    const programs = activeChannel?.programs ?? [];

    const liveIndex = programs.findIndex(programIsLive);
    if (liveIndex !== -1) return liveIndex;

    const closest = getProgramClosestToSameTime(programs);
    return programs.findIndex((program) => program.id === closest?.id);
  }, [activeChannel?.programs]);

  const handleSwipe = useCallback(
    (direction: 'left' | 'right') => {
      const activeChannelIndex = channels.findIndex((channel) => channel === activeChannel);
      const nextItem = channels[direction === 'left' ? activeChannelIndex + 1 : activeChannelIndex - 1];
      if (nextItem) {
        setChannelChangeSource('swipe');
        setChannel(nextItem.id);
      }
    },
    [channels, activeChannel, setChannel],
  );

  useLayoutEffect(() => {
    // Set initial vertical position of timeline
    const container = programContainerRef.current;
    const item = mostRelevantProgramIndex && programsRefs.current[mostRelevantProgramIndex];
    if (!container || !item) return;
    container.scrollTop = item.offsetTop - 20;
  }, [mostRelevantProgramIndex]);

  useEffect(() => {
    const container = channelContainerRef.current;
    const item = activeChannelRef.current;
    if (!container || !item || channelChangeSource === 'click') return;
    container.scrollTo({
      left: item.offsetLeft - container.clientWidth / 2 + item.clientWidth / 2,
      behavior: channelChangeSource === 'load' ? 'instant' : 'smooth',
    });
  }, [activeChannel, channelChangeSource]);

  useEffect(() => {
    const container = programContainerRef.current;

    const handleStart = (e: TouchEvent) => {
      const t = e.touches[0];
      gestureRef.current.startX = t.clientX;
      gestureRef.current.startY = t.clientY;
      gestureRef.current.cancelled = false;
      gestureRef.current.locked = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      const gesture = gestureRef.current;
      const touch = e.touches[0];
      const deltaX = touch.clientX - gesture.startX;
      const deltaY = touch.clientY - gesture.startY;

      if (gesture.cancelled) return;

      const movedHorizontally = Math.abs(deltaX);
      const movedVertically = Math.abs(deltaY);

      const movementDetected = movedHorizontally >= GRACE_DELTA || movedVertically >= GRACE_DELTA;

      if (!gesture.locked && !movementDetected) return;

      if (!gesture.locked) {
        gesture.locked = true;
        gesture.cancelled = movedVertically > movedHorizontally;
      }

      if (gesture.cancelled) return;

      e.preventDefault();
    };

    const handleEnd = (e: TouchEvent) => {
      const gesture = gestureRef.current;
      if (gesture.cancelled) return;

      const deltaX = e.changedTouches[0].clientX - gesture.startX;
      if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;

      if (deltaX > 0) {
        handleSwipe('right');
      } else {
        handleSwipe('left');
      }
    };

    container?.addEventListener('touchstart', handleStart, { passive: true });
    container?.addEventListener('touchmove', handleTouchMove, { passive: false });
    container?.addEventListener('touchend', handleEnd);
    container?.addEventListener('touchcancel', handleEnd);

    return () => {
      container?.removeEventListener('touchstart', handleStart);
      container?.removeEventListener('touchmove', handleTouchMove);
      container?.removeEventListener('touchend', handleEnd);
      container?.removeEventListener('touchcancel', handleEnd);
    };
  }, [activeChannel, handleSwipe]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {isFetching && !channels.length ? (
          <div className={channelsClassnames}>
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className={classNames(styles.channel, styles.loading)} role="presentation" aria-hidden="true" />
            ))}
          </div>
        ) : (
          <div className={channelsClassnames} role="tablist" ref={channelContainerRef}>
            {channels.map((channel) => {
              const isActive = activeChannel?.id === channel.id;
              return (
                <button
                  className={classNames(styles.channel, { [styles.active]: isActive })}
                  key={channel.id}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() =>
                    startChannelTransition(() => {
                      setChannel(channel.id);
                      setChannelChangeSource('click');
                    })
                  }
                  aria-controls="epg-content"
                  id={`channel-${channel.id}`}
                  tabIndex={isActive ? 0 : -1}
                  ref={isActive ? activeChannelRef : null}
                >
                  <Image image={channel.channelLogoImage} className={styles.channelLogo} alt="" />
                  <span>
                    <span className={styles.channelName}>{channel.title}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {activeChannel && (
        <div className={styles.programs} role="tabpanel" aria-labelledby={`channel-${activeChannel.id}`} id="epg-content" ref={programContainerRef}>
          <EpgTimeline channel={activeChannel} programsRef={programsRefs} onProgramClick={onProgramClick} />
        </div>
      )}
      {isFetching && <LoadingOverlay transparentBackground />}
    </div>
  );
};

export default EpgVerticalGuide;
